/**
 * TypeScript types for SIPS automation preferences
 * Used for syncing automation preferences between SIPS and AdvisorPortal
 */

/**
 * Bucket plan preference configuration
 */
export interface BucketPreference {
  bucketType: 'shortTerm' | 'mediumTerm' | 'longTerm' | 'discretionary';
  bucketName: string;
  startYear: number | null;
  endYear: number | null;
  returnPercentage: number;
}

/**
 * Minimum assets band configuration for banded minimum assets type
 */
export interface MinimumAssetsBand {
  bandSequence: number;
  minimumAssetValue: number;
  maximumAssetValue: number | null; // null = unlimited
  minimumPercent: number;
}

/**
 * SIPS automation preferences
 * These settings are stored in SIPS (configCustomer and configBucketPreferences tables)
 * and synced via the /api/sips/customer/{customerId}/automation-preferences endpoint
 */
export interface SipsAutomationPreferences {
  // Advisory fee percentage (0-100)
  advisoryFee: number | null;

  // Time horizon configuration (SC01)
  timeHorizonClientType: 'youngest' | 'oldest' | null;
  timeHorizonAge: number | null;

  // Savings strategy prior to distributions
  savingsStrategyPriorDistributions: 'automatic_makeup' | 'client_defined' | null;

  // Income decrease configuration
  ageToDecreaseIncome: number | null;
  percentToDecreaseIncome: number | null;
  whichClientDecreaseIncome: 'oldest' | 'youngest' | null;
  whenToApplyDecreaseIncome: 'all' | 'needed' | 'never' | null;

  // Inflation limit configuration (SC02)
  ageToLimitInflation: number | null;
  whenToApplyInflationLimit: 'all' | 'needed' | 'never' | null;

  // Earliest plan year before the decrease-income and inflation-limit
  // adjustments may fire (SC03). Blank/0 means no floor.
  minimumPlanYearBeforeAdjustments: number | null;

  // Social Security optimization
  socialSecurityOptimization: 'maximize_savings' | 'file_at_age' | null;
  socialSecurityFileAtAge: number | null; // 62-70

  // Scenario skip preferences
  // skipIncomeOptimization: skip the SC03 Income Optimization scenario entirely
  skipIncomeOptimization: boolean;
  /**
   * @deprecated Retired in SIPS PD-248 — asset allocation (SC04) always runs;
   * SIPS returns false and ignores it on PUT. Kept optional so older
   * runPreferences snapshots still type-check. Never send it.
   */
  skipAssetAllocation?: boolean;

  // Roth conversion analyses (see ROTH_TECHNIQUES). Tax Planning on ⇔ the main
  // Maximize Assets analysis (SC06.01) runs — the one plan results report on —
  // converting up to its max marginal bracket.
  skipRothConversion: boolean;
  maxConversionTaxBracket: number | null;
  // Optional what-if analyses (SIPS PD-248): Minimize Taxes (SC06.01.02) and
  // Maximize Assets to Heirs (SC06.01.03). They can only run while SC06.01
  // runs — skipRothConversion === true forces both skips true
  // (enforceRothInvariant). Optional because an older SIPS GET lacks them.
  skipRothConversionMinimizeTaxes?: boolean;
  maxConversionTaxBracketMinimizeTaxes?: number | null;
  skipRothConversionMaximizeHeirs?: boolean;
  maxConversionTaxBracketMaximizeHeirs?: number | null;

  // Medicare IRMAA (SIPS PD-281): model each client's Part B / Part D surcharge
  // from age 65 in every scenario (MAGI two years back, cliff tiers). Optional
  // because an older SIPS GET lacks it; the PlanScout default is on.
  modelIrmaaExpenses?: boolean;

  // Survivorship analysis configuration (SC06.16)
  lifeExpectancyAgeForSurvivorshipAnalysis: number | null;
  whenToRunSurvivorshipAnalysis: 'never' | 'always' | 'when_needed' | null;
  // With 'when_needed': run only if a pension's survivorship % is <= this threshold
  pensionSurvivorshipThresholdPercent: number | null;

  // Minimum assets at end of plan as percentage of initial
  minimumEndOfPlanAssets: number | null;

  // Minimum assets type: fixed uses minimumEndOfPlanAssets, banded uses minimumAssetsBands
  minimumAssetsType: 'fixed' | 'banded' | null;

  // Minimum assets bands (used when minimumAssetsType is 'banded')
  minimumAssetsBands: MinimumAssetsBand[];

  // Method for calculating minimum assets floor
  // 'sum' adds percentage floor and legacy amount
  // 'max' uses the larger of the two
  minimumAssetsCalculationMethod: 'sum' | 'max' | null;

  // Bucket plan configuration
  bucketPreferences: BucketPreference[];

  // Read-only flag - indicates if automation is enabled in SIPS
  // This is used to show warning messages in the portal
  // Cannot be modified through the portal API
  allowPlanScoutAutomationFlag: boolean;
}

/**
 * API response wrapper for automation preferences
 */
export interface SipsAutomationPreferencesResponse {
  success: boolean;
  data?: SipsAutomationPreferences;
  error?: string;
  message?: string;
}

/**
 * Default values for bucket preferences
 */
export const DEFAULT_BUCKET_PREFERENCES: BucketPreference[] = [
  {
    bucketType: 'shortTerm',
    bucketName: 'Short Term',
    startYear: 1,
    endYear: 4,
    returnPercentage: 3.0
  },
  {
    bucketType: 'mediumTerm',
    bucketName: 'Medium Term',
    startYear: 5,
    endYear: 10,
    returnPercentage: 5.0
  },
  {
    bucketType: 'longTerm',
    bucketName: 'Long Term',
    startYear: 11,
    endYear: null,
    returnPercentage: 7.0
  },
  {
    bucketType: 'discretionary',
    bucketName: 'Discretionary',
    startYear: null,
    endYear: null,
    returnPercentage: 7.0
  }
];

/**
 * Default minimum assets bands (matches SIPS createDefaultBands)
 */
export const DEFAULT_MINIMUM_ASSETS_BANDS: MinimumAssetsBand[] = [
  { bandSequence: 1, minimumAssetValue: 0, maximumAssetValue: 600000, minimumPercent: 50 },
  { bandSequence: 2, minimumAssetValue: 600001, maximumAssetValue: 1000000, minimumPercent: 40 },
  { bandSequence: 3, minimumAssetValue: 1000001, maximumAssetValue: 1500000, minimumPercent: 35 },
  { bandSequence: 4, minimumAssetValue: 1500001, maximumAssetValue: 2000000, minimumPercent: 30 },
  { bandSequence: 5, minimumAssetValue: 2000001, maximumAssetValue: null, minimumPercent: 20 }
];

/**
 * Percent to decrease income options
 */
export const DECREASE_INCOME_PERCENT_OPTIONS = [
  { value: 5, label: '5%' },
  { value: 10, label: '10%' },
  { value: 15, label: '15%' },
  { value: 20, label: '20%' }
];

/**
 * Tax bracket options for Roth conversion
 */
export const TAX_BRACKET_OPTIONS = [
  { value: 10, label: '10%' },
  { value: 12, label: '12%' },
  { value: 22, label: '22%' },
  { value: 24, label: '24%' },
  { value: 32, label: '32%' },
  { value: 35, label: '35%' },
  { value: 37, label: '37%' }
];

/** Marginal bracket SIPS caps a Roth conversion analysis at when none is set. */
export const DEFAULT_CONVERSION_TAX_BRACKET = 37;

export type RothSkipKey =
  | 'skipRothConversion'
  | 'skipRothConversionMinimizeTaxes'
  | 'skipRothConversionMaximizeHeirs';
export type RothBracketKey =
  | 'maxConversionTaxBracket'
  | 'maxConversionTaxBracketMinimizeTaxes'
  | 'maxConversionTaxBracketMaximizeHeirs';

/**
 * One of the Roth IRA conversion analyses SIPS runs for an automated plan
 * (SIPS PD-248). Maximize Assets (SC06.01) is the MAIN analysis — Tax Planning
 * on means it runs, and it is the only one that feeds plan metrics and the
 * client report. Minimize Taxes and Maximize Assets to Heirs are optional
 * what-if analyses that run alongside it and can only run while it runs
 * (see enforceRothInvariant). Each converts up to its own bracket.
 *
 * Mirrors planscout-api/config/rothTechniques.js; labels are SIPS's own names.
 */
export interface RothTechnique {
  id: 'maximize-assets' | 'minimize-taxes' | 'maximize-heirs';
  label: string;
  desc: string;
  skipKey: RothSkipKey;
  bracketKey: RothBracketKey;
  scenario: string;
  /** 'main' = SC06.01 (mandatory while Tax Planning is on); 'what-if' = optional. */
  role: 'main' | 'what-if';
}

export const ROTH_TECHNIQUES: readonly RothTechnique[] = [
  {
    id: 'maximize-assets',
    label: 'Maximize Assets',
    desc: 'Keep conversions that grow end-of-plan assets without raising lifetime taxes.',
    skipKey: 'skipRothConversion',
    bracketKey: 'maxConversionTaxBracket',
    scenario: 'SC06.01',
    role: 'main'
  },
  {
    id: 'minimize-taxes',
    label: 'Minimize Taxes',
    desc: 'Keep conversions that lower lifetime taxes.',
    skipKey: 'skipRothConversionMinimizeTaxes',
    bracketKey: 'maxConversionTaxBracketMinimizeTaxes',
    scenario: 'SC06.01.02',
    role: 'what-if'
  },
  {
    id: 'maximize-heirs',
    label: 'Maximize Assets to Heirs',
    desc: 'Keep conversions that increase after-tax assets left to heirs.',
    skipKey: 'skipRothConversionMaximizeHeirs',
    bracketKey: 'maxConversionTaxBracketMaximizeHeirs',
    scenario: 'SC06.01.03',
    role: 'what-if'
  }
];

const mainRothTechnique = ROTH_TECHNIQUES.find((t) => t.role === 'main');
if (!mainRothTechnique) throw new Error('ROTH_TECHNIQUES must declare exactly one main technique');
/** The main Roth analysis (SC06.01) — runs whenever Tax Planning is on. */
export const ROTH_MAIN: RothTechnique = mainRothTechnique;
/** The optional what-if analyses (SC06.01.02 / SC06.01.03). */
export const ROTH_WHAT_IFS: readonly RothTechnique[] = ROTH_TECHNIQUES.filter((t) => t.role === 'what-if');

/**
 * Tax Planning on ⇔ the main analysis runs. Keep in lockstep with
 * deriveRunOptimizations() in planscout-api/utils/preferenceOverrides.js.
 */
export const rothOn = (prefs: Partial<SipsAutomationPreferences>): boolean =>
  prefs.skipRothConversion !== true;

/**
 * Whether a SIPS preference set knows this technique at all. A release before
 * PD-248 returns no key for the two what-if analyses — and rejects them as
 * overrides / on PUT — so callers must neither offer nor send them.
 */
export const rothTechniqueSupported = (known: Partial<SipsAutomationPreferences>, t: RothTechnique): boolean =>
  Object.prototype.hasOwnProperty.call(known, t.skipKey);

/**
 * The Roth invariant: skipping the main analysis skips the what-ifs too
 * (`skipRothConversion === true ⇒ both what-if skips true`). Applied wherever
 * preferences are seeded, toggled or saved so no draft/stored set can run a
 * what-if without SC06.01. Only touches what-if keys `known` carries (defaults
 * to `prefs` itself) so an older SIPS never receives a key it would reject.
 * Returns `prefs` unchanged when the main analysis runs.
 */
export const enforceRothInvariant = <T extends Partial<SipsAutomationPreferences>>(
  prefs: T,
  known: Partial<SipsAutomationPreferences> = prefs
): T => {
  if (prefs.skipRothConversion !== true) return prefs;
  const next: Partial<SipsAutomationPreferences> = { ...prefs };
  for (const t of ROTH_WHAT_IFS) {
    if (rothTechniqueSupported(known, t)) next[t.skipKey] = true;
  }
  return next as T;
};

/**
 * Per-plan preference overrides — a sparse subset of SipsAutomationPreferences
 * sent with a single create-case call. Absent fields inherit the advisor's SIPS
 * preferences; present values win in BOTH directions for that run only (e.g.
 * skipRothConversionMinimizeTaxes: true skips the Minimize Taxes what-if for an
 * advisor whose defaults run it). The draft is run through enforceRothInvariant
 * before diffing, so a run that skips SC06.01 sends the what-if skips too.
 * Never persisted to the advisor's preferences. advisoryFee and
 * allowPlanScoutAutomationFlag are not overridable per-plan.
 */
export type PlanPreferenceOverrides = Partial<
  Omit<SipsAutomationPreferences, 'advisoryFee' | 'allowPlanScoutAutomationFlag'>
>;
